import type { Mark } from '@atlaskit/react-ufo/common';
import { addCustomTiming } from '@atlaskit/react-ufo/interaction-metrics';

/**
 * Converts UFO custom marks into custom timings for the active UFO interaction.
 *
 * This is the UFO-native equivalent of `addAllBM3TimingsToUFO` from
 * `@confluence/browser-metrics`. Instead of receiving a flat `BM3Marks` map
 * (`{ [key: string]: number }`), this function operates on the UFO `Mark[]`
 * array (filtering for `type === 'custom'`) and derives the same segment
 * start/end timing pairs.
 *
 * Matched patterns:
 * - `segment-start:<name>` / `segment-end:<name>` → timing key `wf/react/<name>`
 * - `wf<…>.start` / `wf<…>.end` → timing key `wf<…>`
 */
export function addCustomTimingsFromMarks(interactionId: string, marks: Mark[]): void {
	// Build a lookup map from custom mark names to their timestamps
	const markMap: { [key: string]: number } = {};
	for (const mark of marks) {
		if (mark.type === 'custom') {
			markMap[mark.name] = mark.time;
		}
	}

	const timings: { [key: string]: { startTime: number; endTime: number } } = {};

	for (const name of Object.keys(markMap)) {
		if (name.startsWith('segment-start')) {
			// Split is unavoidable here, as we need to extract the segment name from the mark name.
			// eslint-disable-next-line @atlassian/perf-linting/no-expensive-split-replace
			const segmentName = name.split(':')[1];
			const key = `wf/react/${segmentName}`;
			const endMark = `segment-end:${segmentName}`;
			const startTime = markMap[name];
			const endTime = markMap[endMark];

			if (typeof startTime === 'number' && typeof endTime === 'number') {
				timings[key] = { startTime, endTime };
			}
		}
	}

	addCustomTiming(interactionId, [], timings);
}
